import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useParams } from "react-router-dom";

// styles
import "./style.scss";

// layout components
import ErrorBoundary from "engine/ErrorBoundary";

// antd component
import { Pagination } from "antd";

// Communication components
import { MessagesList, CreateMessage } from "components/Communications";

// utils
import api from "utils/api";
import { getObjId } from "utils/helpers/helper.general";
import { useAsync, useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

const DiscussionTab = ({ discussionId, onUpdateMark, className }) => {
  const childClassNames = classNames("discussion-tab", className);

  const newKey = "new";

  const { id: initId } = useParams();

  const {
    data: dataList,
    isLoading: isLoadingList,
    isSuccess: isSuccessList,
    run: getList,
  } = useAsync();

  const {
    isLoading: isLoadingMessage,
    isSuccess: isSuccessMessage,
    run: createMessage,
  } = useAsync();

  const [firstLoad, setFirstLoad] = useState(false);
  const [messagesArray, messagesArrayRef, setMessagesArray] = useRefState([]);
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const [communicationId, setCommunicationId] = useState(null);
  const [currentPage, setCurrentPage] = useState(dataList?.page || 1);
  const [currentMessage, setCurrentMessage] = useState(false);
  const [scrollToMsg, setScrollToMsg] = useState(false);

  useEffect(() => {
    return () => {
      setMessagesArray([]);
      setCurrentMessage(false);
      setCurrentPage(1);
    };
  }, []);

  // Because history not trigger update component
  useEffect(() => {
    if (!!initId && initId !== newKey && !!discussionId) {
      setCommunicationId(initId);
      // Loading data for rendering in a component
      getMessages({ commId: initId });
      setFirstLoad(true);
      setScrollToMsg(false);
    }
  }, [discussionId, initId]);

  // Set lodaded data for rendering in a component
  useEffect(() => {
    if (!isLoadingList && !!dataList) {
      setMessagesArray(dataList?.docs);
      setFirstLoad(false);
      if (dataList?.page != currentPage) {
        setCurrentPage(dataList?.page);
      }
      // mark discussion as read when component mounted
      if (firstLoad) {
        markAsReadHandle();
      }
    }
  }, [dataList]);

  // mark discussion as read (only for all admins)
  const markAsReadHandle = useCallback(async () => {
    if (!!discussionId) {
      let result = false;
      api.discussions.markAsRead(getObjId(discussionId)).then(finalRes => {
        if (!!finalRes?.readers) {
          onUpdateMark({
            readers: finalRes?.readers,
            readersFiltered: finalRes?.readersFiltered,
          });
          result = {
            readers: finalRes?.readers,
            readersFiltered: finalRes?.readersFiltered,
          };
        }
      });
      return result;
    } else {
      return null;
    }
  }, [discussionId]);

  const getMessages = useCallback(
    ({ page = 1, scrollToMsg = null, commId = null }) => {
      setCurrentMessage(false);
      const ID = commId
        ? commId
        : communicationId || (!!initId && initId !== newKey && initId);

      if (!!ID) {
        const requestData = {
          communicationId: ID,
          page,
          sortBy: -1,
          scrollToMsg,
        };
        getList(api.discussions.getDiscussionMessagesById(requestData));
      } else {
        return null;
      }
    },
    [communicationId, initId, discussionId],
  );

  const onSendMessage = savedData => {
    if (!!savedData) {
      setCurrentMessage(false);
      setSendMessageLoading(true);

      api.discussions
        .sendMessage({
          communication: initId,
          discussion: discussionId,
          type: reply ? "replay" : "default",
          replayOn: reply?._id,
          ...savedData,
        })
        .then(resultMessage => {
          setCurrentMessage(resultMessage);
          setSendMessageLoading(false);
          setReply(null);
          setScrollToMsg(false);
          if (currentPage != 1) {
            getMessages({ page: 1 });
          }
          setMessagesArray(prev => {
            let prevDataDocs = prev || [];
            const hastMessage = prevDataDocs?.find(
              msg => msg?._id == resultMessage?._id,
            );
            if (!hastMessage) {
              prevDataDocs.push(resultMessage);
            }
            return prevDataDocs;
          });
        })
        .catch(e => {
          setCurrentMessage(false);
          setSendMessageLoading(false);
          console.error(e);
          setReply(null);
          setScrollToMsg(false);
        });
    }
  };

  const handleMessagesPagination = page => {
    setCurrentPage(page);
    getMessages({ page });
  };

  const onRelyHandle = useCallback(
    messageId => {
      if (!!messageId) {
        setCurrentMessage(false);
        setScrollToMsg(messageId);
        getMessages({ page: currentPage, scrollToMsg: messageId });
      }
    },
    [currentPage],
  );

  return (
    !!discussionId && (
      <div className={childClassNames}>
        <ErrorBoundary>
          {!!dataList && (
            <div className="messages--pagination">
              <Pagination
                defaultCurrent={dataList?.page || 1}
                pageSize={dataList?.limit || 10}
                current={dataList?.page}
                total={dataList.totalDocs}
                showSizeChanger={false}
                onChange={handleMessagesPagination}
              />
            </div>
          )}
          <MessagesList
            loading={isLoadingList}
            success={isSuccessList}
            firstLoad={firstLoad}
            list={firstLoad ? dataList?.docs : messagesArray}
            newMessage={currentMessage}
            reply={reply}
            scrollToMsg={scrollToMsg}
            setScrollToMsg={setScrollToMsg}
            onReply={onRelyHandle}
            setReply={setReply}
          />
          <CreateMessage
            loading={sendMessageLoading}
            disabled={isLoadingList}
            reply={reply}
            setReply={setReply}
            onSend={onSendMessage}
          />
        </ErrorBoundary>
      </div>
    )
  );
};

DiscussionTab.defaultProps = {};
DiscussionTab.propTypes = {
  discussionId: PropTypes.string,
};

export default DiscussionTab;
